import React, { useEffect, useState } from 'react';
import { useWeather } from '../../context';
import { useAuth } from '../../hooks/useAuth';
import { fetchSavedSearches, deleteSavedSearch } from '../../services/searchService';
import { X } from 'lucide-react';
import { SavedSearch } from '../../types/search';
import { zoomToPolygon } from '../../utils/mapHelpers';

export interface SavedSearchesProps {
  onClose: () => void;
}

export function SavedSearches({ onClose }: SavedSearchesProps) {
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const { user } = useAuth();
  const { 
    setModelSelect, 
    setParameterSelect, 
    setStartDate, 
    setEndDate,
    setResolution,
    mapInstance,
    drawInstance 
  } = useWeather();

  useEffect(() => {
    if (user) {
      loadSearches();
    }
  }, [user]);

  const loadSearches = async () => {
    try {
      setLoading(true);
      const data = await fetchSavedSearches(user!.id);
      setSearches(data);
    } catch (err) {
      setError('Failed to load saved searches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSearch = (search: SavedSearch) => {
    setModelSelect(search.model);
    setParameterSelect(search.parameter);
    setStartDate(search.start_date);
    setEndDate(search.end_date);
    setResolution(search.resolution);

    if (drawInstance && mapInstance) {
      drawInstance.deleteAll();
      drawInstance.add(search.polygon);
      zoomToPolygon(mapInstance, search.polygon);
    }

    onClose();
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSavedSearch(id);
      await loadSearches();
    } catch (err) {
      console.error('Error deleting search:', err);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="glass-panel p-6">Loading...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="glass-panel p-6 w-[600px] max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Saved Searches</h2>
          <button onClick={onClose}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="text-[var(--danger-color)] mb-4">
            {error}
          </div>
        )}

        {searches.length === 0 ? (
          <div className="text-center text-[var(--secondary-text)] py-8">
            No saved searches found
          </div>
        ) : (
          <div className="space-y-4">
            {searches.map((search) => (
              <div
                key={search.id}
                className="glass-panel p-4 flex justify-between items-center"
              >
                <div>
                  <h3 className="font-semibold">{search.name}</h3>
                  <p className="text-sm text-[var(--secondary-text)]">
                    {search.model} - {search.parameter}
                  </p>
                  <p className="text-sm text-[var(--secondary-text)]">
                    {search.start_date} to {search.end_date}
                  </p>
                  <p className="text-sm text-[var(--secondary-text)]">
                    Resolution: {search.resolution}°
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadSearch(search)}
                    className="btn btn-sm"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => handleDelete(search.id)}
                    className="btn btn-sm btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}